import { useEffect, useMemo } from 'react';

import {
	useInlineReactionActions,
	useOptimisticReactionMarkerMap,
} from '../inlineReactionStore.ts';

export type InlineReactionMarkersInput = {
	/** Server-confirmed markerRefs from `useActiveInlineData`. */
	reactionMarkerRefs: string[];
	/** Server-confirmed `{markerRef → commentId}` from `useActiveInlineData`. */
	reactionMarkerMap: Record<string, string>;
};

export type UseInlineReactionMarkersResult = {
	mergedReactionMarkerRefs: string[];
	mergedReactionMarkerMap: Record<string, string>;
};

/**
 * Merges server-confirmed reaction markers with the optimistic map
 * populated by `useApplyInlineReactionAnnotation` immediately after a successful create,
 * and drains the optimistic store once the server-confirmed map catches up.
 */
export const useInlineReactionMarkers = ({
	reactionMarkerRefs,
	reactionMarkerMap,
}: InlineReactionMarkersInput): UseInlineReactionMarkersResult => {
	const optimisticReactionMarkerMap = useOptimisticReactionMarkerMap();
	const { pruneOptimisticReactions } = useInlineReactionActions();

	// Drain optimistic entries once the server-confirmed map contains them so
	// the optimistic store does not grow unbounded. `pruneOptimisticReactions`
	// is itself a no-op when nothing needs pruning.
	useEffect(() => {
		if (Object.keys(reactionMarkerMap).length > 0) {
			void pruneOptimisticReactions(Object.keys(reactionMarkerMap));
		}
	}, [reactionMarkerMap, pruneOptimisticReactions]);

	const mergedReactionMarkerMap = useMemo(() => {
		if (Object.keys(optimisticReactionMarkerMap).length === 0) {
			return reactionMarkerMap;
		}
		// Server map wins on key conflicts.
		return { ...optimisticReactionMarkerMap, ...reactionMarkerMap };
	}, [reactionMarkerMap, optimisticReactionMarkerMap]);

	const mergedReactionMarkerRefs = useMemo(() => {
		const optimisticRefs = Object.keys(optimisticReactionMarkerMap);
		if (optimisticRefs.length === 0) {
			return reactionMarkerRefs;
		}
		const set = new Set(reactionMarkerRefs);
		for (const ref of optimisticRefs) {
			set.add(ref);
		}
		return Array.from(set);
	}, [reactionMarkerRefs, optimisticReactionMarkerMap]);

	return {
		mergedReactionMarkerRefs,
		mergedReactionMarkerMap,
	};
};
